/**
 * ChatPresence — keeps the team-chat socket connected app-wide and surfaces
 * incoming messages everywhere. Renders nothing.
 *
 * WHY THIS EXISTS (the dead-badge bug):
 *   chat.service.ts has no unread endpoint, so the store's unreadCounts only
 *   grows from live "newMessage" socket events. If the socket is only
 *   initialized when the chat page mounts, then OFF that page there's no
 *   socket → no newMessage → the sidebar badge never moves and you can't tell
 *   a message arrived unless you're already on the chat screen. Keeping the
 *   socket connected for the whole session fixes that; the Sidebar badge and a
 *   toast then fire anywhere in the app.
 *
 * MOUNTING — IMPORTANT:
 *   This is mounted inside AuthedLayout, which React re-instantiates on every
 *   route change, so this component unmounts/remounts on each navigation. To
 *   keep that from re-firing the "you have N unread" summary toast on every
 *   page change, the notify state lives at MODULE scope (survives remounts),
 *   not in a component ref. Reset on logout so the next login summarises again.
 *
 *   Safe to mount even if the chat page ALSO calls initializeSocket — the
 *   store's initializeSocket has a dedup guard, so there's only ever one
 *   socket. Supersedes the old ChatUnreadToaster (delete that one).
 *
 * NOTE: surfaces messages that arrive WHILE the app is open. Unread that
 * accumulated before login needs a backend /chat/unread endpoint to hydrate
 * on connect — not wired here.
 */
import { useEffectiveRole } from "@/components/auth/RouteGuards";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useChatUnread } from "@/hooks/useChatUnread";
import useChatStore from "@/stores/chatStore";
import useAuthStore, { isCashier } from "@/stores/authStore";

// ── Module-scope notify state (survives the per-route remount) ──────────
// One-time login summary per session; last unread total we've reacted to.
let loginSummaryShown = false;
let lastSeenUnread: number | null = null;

function resetPresenceState() {
  loginSummaryShown = false;
  lastSeenUnread = null;
}

const ChatPresence: React.FC = () => {
  const navigate = useNavigate();

  const token = useAuthStore((s) => s.token);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const role = useEffectiveRole();

  const initializeSocket = useChatStore((s) => s.initializeSocket);
  const disconnectSocket = useChatStore((s) => s.disconnectSocket);

  const unread = useChatUnread();

  const chatPath = role && isCashier(role) ? "/cashier/chat" : "/team-chat";
  const goToChat = () => navigate(chatPath);

  // 1. Keep the socket connected app-wide while authenticated.
  //    initializeSocket is idempotent (dedup guard in the store), so calling it
  //    on every remount is a safe no-op once connected.
  useEffect(() => {
    if (isAuthenticated && token) {
      initializeSocket(token);
    } else {
      disconnectSocket();
      resetPresenceState(); // next login should summarise again
    }
  }, [isAuthenticated, token, initializeSocket, disconnectSocket]);

  // 2. Notify on unread changes (module-scope state → remount-safe).
  //    - First reading of the session = login summary (toast if already > 0).
  //    - Any later increase = a message just arrived → toast.
  //    Own sent messages never increment unread (handled in the store), so this
  //    only fires for messages from someone else to a non-open chat.
  useEffect(() => {
    if (lastSeenUnread === null) {
      lastSeenUnread = unread;
      if (!loginSummaryShown && unread > 0) {
        loginSummaryShown = true;
        toast.message(
          `You have ${unread} unread message${unread === 1 ? "" : "s"}`,
          {
            description: "Your team left messages in Team Chat.",
            action: { label: "Open chat", onClick: goToChat },
          },
        );
      }
      return;
    }

    if (unread > lastSeenUnread) {
      const delta = unread - lastSeenUnread;
      toast.message(delta === 1 ? "New message" : `${delta} new messages`, {
        description: "You have a new team message.",
        action: { label: "Open chat", onClick: goToChat },
      });
    }
    lastSeenUnread = unread;
    // goToChat identity changes with role/navigate; intentionally excluded so
    // the comparison only re-runs when the unread total actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unread]);

  return null;
};

export default ChatPresence;
